import { createContext, useContext, useState } from 'react';

import { useSessionStorage } from '@betterment/js-runtime';
import { type InferType, ObjectSchema } from 'yup';

/**
 * Flow config types
 *
 * AnyStepConfig -- type for step in config object
 * AnyFlowConfig -- type for whole config object
 * AnyCrossFlowConfigs -- type for cross-flow config object
 * HasOnlyAllowedKeys -- type for validating step in config object
 * HasValidDependencies -- type for validating dependencies config object
 * ValidateFlowConfig -- type for validating config object
 */
type AnyStepConfig = {
  schema: ObjectSchema<Record<string, unknown>>;
  dependencies: ObjectSchema<Record<string, Record<string, unknown> | null | undefined>>;
};
type AnyFlowConfig = Record<string, AnyStepConfig>;
type AnyCrossFlowConfigs = Record<string, AnyFlowConfig>;

/**
 * Phantom type tag — used as a symbol key so it is invisible to string index
 * signatures and does not affect AnyFlowConfig assignability.
 */
declare const crossFlowConfigsTag: unique symbol;
type WithCrossFlowConfigs<Config, CrossFlowConfigs extends AnyCrossFlowConfigs> = Config & {
  readonly [crossFlowConfigsTag]?: CrossFlowConfigs;
};
type InferCrossFlowConfigs<FlowConfig> = FlowConfig extends { readonly [crossFlowConfigsTag]?: infer C }
  ? C extends AnyCrossFlowConfigs
    ? C
    : Record<never, never>
  : Record<never, never>;

type HasOnlyAllowedKeys<StepConfig> = keyof StepConfig extends keyof AnyStepConfig
  ? StepConfig
  : {
      error: 'Invalid key in step config';
      invalidKeys: Exclude<keyof StepConfig, keyof AnyStepConfig>;
    };
type HasValidCrossFlowStepKeys<
  DependencyConfig,
  CrossFlowConfigs extends AnyCrossFlowConfigs,
> = keyof DependencyConfig & keyof CrossFlowConfigs extends never
  ? true
  : {
        [K in keyof DependencyConfig & keyof CrossFlowConfigs]: keyof NonNullable<
          DependencyConfig[K]
        > extends keyof CrossFlowConfigs[K]
          ? true
          : false;
      }[keyof DependencyConfig & keyof CrossFlowConfigs] extends true
    ? true
    : false;
type HasValidDependencies<FlowConfig, CrossFlowConfigs extends AnyCrossFlowConfigs, StepConfig> = StepConfig extends {
  dependencies: ObjectSchema<infer DependencyConfig>;
}
  ? keyof DependencyConfig extends keyof FlowConfig | keyof CrossFlowConfigs
    ? HasValidCrossFlowStepKeys<DependencyConfig, CrossFlowConfigs> extends true
      ? StepConfig
      : {
          error: 'Invalid step key in cross-flow dependency';
          invalidStepKeys: {
            [K in keyof DependencyConfig & keyof CrossFlowConfigs]: Exclude<
              keyof NonNullable<DependencyConfig[K]>,
              keyof CrossFlowConfigs[K]
            >;
          };
        }
    : {
        error: 'Invalid dependency key';
        invalidKeys: Exclude<keyof DependencyConfig, keyof FlowConfig | keyof CrossFlowConfigs>;
      }
  : never;
type ValidateFlowConfig<
  FlowConfig extends AnyFlowConfig,
  CrossFlowConfigs extends AnyCrossFlowConfigs = Record<never, never>,
> = {
  [Step in keyof FlowConfig]: HasOnlyAllowedKeys<FlowConfig[Step]> extends FlowConfig[Step]
    ? HasValidDependencies<FlowConfig, CrossFlowConfigs, FlowConfig[Step]>
    : HasOnlyAllowedKeys<FlowConfig[Step]>;
};

/**
 * useStep types
 *
 * Values -- type for values per step
 * Dependencies -- type for dependencies per step
 * AnyStep -- type for step config
 * UseStep -- type for useStep return
 */
type Values<FlowConfig extends AnyFlowConfig> = {
  [Step in keyof FlowConfig]: InferType<FlowConfig[Step]['schema']>;
};
type Dependencies<FlowConfig extends AnyFlowConfig> = {
  [Step in keyof FlowConfig]: InferType<FlowConfig[Step]['dependencies']>;
};
type AnyStep<FlowConfig extends AnyFlowConfig> = keyof FlowConfig;
type UseStep<FlowConfig extends AnyFlowConfig, Step extends AnyStep<FlowConfig>> = {
  values: Values<FlowConfig>[Step] | null;
  dependencies: Dependencies<FlowConfig>[Step];
  commit: (values: Values<FlowConfig>[Step]) => void;
};

/**
 * Type utils
 *
 * StepValues -- type for inferring type per given step
 */
export type StepValues<FlowConfig extends AnyFlowConfig, Step extends AnyStep<FlowConfig>> = InferType<
  FlowConfig[Step]['schema']
>;

/**
 * readSessionStorage
 *
 * Internal utility to synchronously read a value from session storage respecting
 * the bmt-namespaced key format and TTL used by useSessionStorage.
 *
 * this util is needed due to the rule of hook limitations of reading session storage in a loop
 */
const readSessionStorage = <T extends object>(key: string): T | null => {
  try {
    const stored = sessionStorage.getItem(`bmt.${key}`);
    if (!stored) return null;
    const { expiry, value } = JSON.parse(stored) as { expiry: number; value: T };
    if (Date.now() >= expiry) return null;
    return value;
  } catch {
    return null;
  }
};

/**
 * createConfig
 * @param config
 * @param _crossFlowConfigs -- optional cross-flow configs used only for compile-time type validation.
 *   The cross-flow configs are embedded in the return type as a phantom type so that
 *   createFlowContext can infer them automatically without needing them passed again.
 *
 * util for creating a flow config object that provides type safety and auto-complete.
 * Pass cross-flow configs as the second argument to allow dependency keys that reference other flows.
 */
export const createConfig = <
  const Config extends AnyFlowConfig,
  const CrossFlowConfigs extends AnyCrossFlowConfigs = Record<never, never>,
>(
  config: Config & ValidateFlowConfig<Config, CrossFlowConfigs>,
  _crossFlowConfigs?: CrossFlowConfigs
): WithCrossFlowConfigs<Config, CrossFlowConfigs> => config as WithCrossFlowConfigs<Config, CrossFlowConfigs>;

/**
 * createFlowContext
 * @param flowConfig
 *
 * factory method to create a typed useStep hook and FlowProvider
 */
export const createFlowContext = <
  FlowConfig extends AnyFlowConfig,
  CrossFlowConfigs extends AnyCrossFlowConfigs = InferCrossFlowConfigs<FlowConfig>,
>(
  flowConfig: FlowConfig
) => {
  const useFlowStorage = ({
    flowId,
    initialValues,
  }: {
    flowId?: string;
    initialValues?: Partial<Values<FlowConfig>>;
  }): [Partial<Values<FlowConfig>>, (newFlowValues: Partial<Values<FlowConfig>>) => void] => {
    const { values, saveValues } = useSessionStorage<Partial<Values<FlowConfig>>>(
      `bmt-flow-${flowId}`,
      initialValues || {}
    );
    const [flowValues, setFlowValues] = useState<Partial<Values<FlowConfig>>>(values ?? {});

    const saveFlowValues = (newFlowValues: Partial<Values<FlowConfig>>) => {
      setFlowValues(newFlowValues);
      if (flowId) saveValues(newFlowValues);
    };

    return [flowValues, saveFlowValues];
  };

  const FlowContext = createContext<{
    flowValues: Partial<Values<FlowConfig>>;
    saveFlowValues: (values: Partial<Values<FlowConfig>>) => void;
    crossFlowValues: Partial<Record<keyof CrossFlowConfigs, object | null>>;
  } | null>(null);

  const FlowProvider: React.FC<
    React.PropsWithChildren<{
      flowId?: string;
      initialValues?: Partial<Values<FlowConfig>>;
      crossFlowIds?: Partial<Record<keyof CrossFlowConfigs, string>>;
    }>
  > = ({ children, flowId, initialValues, crossFlowIds = {} }) => {
    const [flowValues, saveFlowValues] = useFlowStorage({ flowId, initialValues });

    const crossFlowValues = Object.fromEntries(
      Object.entries(crossFlowIds).map(([key, id]) => [key, id ? readSessionStorage(`bmt-flow-${id}`) : null])
    ) as Partial<Record<keyof CrossFlowConfigs, object | null>>;

    return (
      <FlowContext.Provider
        // eslint-disable-next-line react/jsx-no-constructed-context-values
        value={{
          flowValues,
          saveFlowValues,
          crossFlowValues,
        }}
      >
        {children}
      </FlowContext.Provider>
    );
  };

  const useFlow = () => {
    const context = useContext(FlowContext);
    if (!context) {
      throw new Error('useFlow must be used within FlowProvider');
    }

    return context;
  };

  const useStep = <Step extends AnyStep<FlowConfig>>(stepName: Step): UseStep<FlowConfig, Step> => {
    const { flowValues, saveFlowValues, crossFlowValues } = useFlow();

    const commit = (stepValues: Values<FlowConfig>[Step]) => {
      try {
        flowConfig[stepName]!.schema.validateSync(stepValues);
      } catch (e) {
        throw new Error('Step values did not pass validation', { cause: e });
      }

      const updatedFlowValues = { ...flowValues, [stepName]: stepValues };
      saveFlowValues(updatedFlowValues);
    };

    const getStepValues = () => {
      try {
        return flowConfig[stepName]!.schema.validateSync(flowValues[stepName]);
      } catch {
        return null;
      }
    };

    const getDependencies = () => {
      try {
        return flowConfig[stepName]!.dependencies.validateSync({
          ...flowValues,
          ...crossFlowValues,
        });
      } catch (e) {
        throw new Error('Step does not meet dependency requirements', { cause: e });
      }
    };

    return {
      commit,
      values: getStepValues(),
      dependencies: getDependencies(),
    };
  };

  return {
    FlowProvider,
    useStep,
  };
};
